import React, { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { adminListConsults } from "../lib/api";

export default function AdminConsultsPage() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListConsults(q ? { q } : {});
      setItems(data.consults || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load consults");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    if (!items.length) return toast("Nothing to export.");
    const rows = [
      ["ID", "Name", "Email", "Phone", "Goal", "Brief", "Created At"],
      ...items.map((c) => [c.id, c.name, c.email, c.phone, c.goal || "", (c.brief || "").replace(/\r?\n/g, " "), c.created_at || ""]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `authoros-consults-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} consults.`);
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Studio admin</div>
          <h1 className="mt-2 font-serif-display text-[34px] font-medium leading-[0.98] tracking-tight sm:text-[42px] lg:text-[46px]">
            Consult submissions<span className="text-brand">.</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={load}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-hair text-brand-ivory hover:border-brand hover:text-brand"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-white hover:bg-[#ff5c26]"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="mt-8 relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-mute" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by name, email or goal"
          className="h-11 w-full rounded-md border border-brand-hair bg-brand-panel px-10 text-[13px] text-brand-ivory placeholder:text-brand-mute focus:border-brand"
        />
      </div>

      <div className="mt-6 space-y-3">
        {items.length === 0 ? (
          <div className="rounded-xl border border-brand-hair bg-brand-panel px-6 py-10 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            {loading ? "Loading..." : "No submissions yet."}
          </div>
        ) : (
          items.map((c) => (
            <div key={c.id} className="rounded-xl border border-brand-hair bg-brand-panel p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
                    <span>{c.id}</span>
                    {c.goal && <span className="rounded bg-brand/15 px-2 py-0.5 text-brand">{c.goal}</span>}
                  </div>
                  <div className="mt-1 font-serif-display text-[20px] leading-tight text-brand-ivory">{c.name}</div>
                  <div className="font-mono text-[11.5px] text-brand-ivory/70">{c.email} · {c.phone}</div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
                  {c.created_at ? new Date(c.created_at).toLocaleString("en-IN") : ""}
                </div>
              </div>
              {c.brief && <p className="mt-3 text-[13px] leading-relaxed text-brand-ivory/80">{c.brief}</p>}
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
